import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    AlertTriangle,
    Camera,
    Crosshair,
    Loader2,
    MapPin,
    Plus,
    RotateCcw,
    X,
} from 'lucide-react';

import { checkDuplicatePhone, createLead } from '@/app/services/leadService';
import { uploadLeadPhoto } from '@/app/services/attachmentService';
import { useAuthStore } from '@/app/store/authStore';
import {
    buildMapsUrl,
    formatCoordinates,
    getCurrentCoordinates,
    type Coordinates,
} from '@/app/lib/geolocation';
import { clearLeadDraft, loadLeadDraft, saveLeadDraft } from '@/app/lib/leadDraft';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileInput } from '@/components/ui/MobileInput';
import { MobileTextarea } from '@/components/ui/MobileTextarea';
import { cn } from '@/lib/utils';
import {
    LEAD_SOURCE_LABELS,
    LEAD_TYPE_LABELS,
    type DuplicateWarning,
    type LeadSource,
    type LeadType,
} from '@/app/types/lead';
import { addDaysISO, todayISO } from './leadUi';

const LEAD_TYPE_CHIPS: LeadType[] = [
    'restaurant',
    'bakery',
    'caterer',
    'retail_shop',
    'cloud_kitchen',
    'other',
];
const SOURCE_CHIPS: LeadSource[] = ['field_visit', 'whatsapp', 'referral', 'call', 'walk_in'];

interface FormValues {
    business_name: string;
    phone: string;
    contact_person: string;
    alt_phone: string;
    lead_type: string;
    area: string;
    pincode: string;
    address: string;
    source: string;
    estimated_monthly_value: string;
    notes: string;
    next_follow_up_date: string;
    geo_location: string;
}

const EMPTY: FormValues = {
    business_name: '',
    phone: '',
    contact_person: '',
    alt_phone: '',
    lead_type: '',
    area: '',
    pincode: '',
    address: '',
    source: 'field_visit',
    estimated_monthly_value: '',
    notes: '',
    next_follow_up_date: '',
    geo_location: '',
};

export default function NewLeadPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);
    const siteId = user?.sites?.[0]?.id ?? null;

    const [values, setValues] = useState<FormValues>(EMPTY);
    const [showAltPhone, setShowAltPhone] = useState(false);
    const [duplicate, setDuplicate] = useState<DuplicateWarning | null>(null);
    const [photos, setPhotos] = useState<File[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [restorePrompt, setRestorePrompt] = useState<FormValues | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Location capture
    const [locating, setLocating] = useState(false);
    const [captured, setCaptured] = useState<Coordinates | null>(null);

    const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
        setValues((prev) => ({ ...prev, [key]: value }));

    // Offer back anything a previous failed attempt left behind. See lib/leadDraft.ts.
    useEffect(() => {
        const draft = loadLeadDraft();
        if (draft) setRestorePrompt(draft.values as unknown as FormValues);
    }, []);

    useEffect(() => {
        saveLeadDraft(values as unknown as Record<string, unknown>);
    }, [values]);

    const runDuplicateCheck = async () => {
        const digits = values.phone.replace(/\D/g, '');
        if (digits.length < 10) {
            setDuplicate(null);
            return;
        }
        try {
            setDuplicate(await checkDuplicatePhone(values.phone));
        } catch {
            // Advisory only — a failed lookup must never get in the way of capture.
            setDuplicate(null);
        }
    };

    const handleCaptureLocation = async () => {
        setLocating(true);
        try {
            setCaptured(await getCurrentCoordinates());
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not get location');
        } finally {
            setLocating(false);
        }
    };

    const saveCapturedLocation = () => {
        if (!captured) return;
        set('geo_location', formatCoordinates(captured.lat, captured.lng));
        setCaptured(null);
        toast.success('Location attached');
    };

    const mutation = useMutation({
        mutationFn: async () => {
            const payload = {
                business_name: values.business_name.trim(),
                phone: values.phone.trim() || undefined,
                contact_person: values.contact_person.trim() || undefined,
                alt_phone: values.alt_phone.trim() || undefined,
                lead_type: (values.lead_type || undefined) as LeadType | undefined,
                area: values.area.trim() || undefined,
                pincode: values.pincode.trim() || undefined,
                address: values.address.trim() || undefined,
                source: values.source as LeadSource,
                estimated_monthly_value: values.estimated_monthly_value || undefined,
                notes: values.notes.trim() || undefined,
                next_follow_up_date: values.next_follow_up_date || undefined,
                geo_location: values.geo_location || undefined,
                site_id: siteId,
            };
            const created = await createLead(payload);

            // Photos upload after the lead exists. A photo failure must never cost
            // the lead itself — it is reported and retried from the detail screen.
            let failed = 0;
            for (const photo of photos) {
                try {
                    await uploadLeadPhoto(created.lead.id, photo, siteId);
                } catch {
                    failed += 1;
                }
            }
            return { created, failed };
        },
        onSuccess: ({ created, failed }) => {
            clearLeadDraft();
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead-summary'] });
            toast.success('Lead saved');
            if (failed > 0) {
                toast.error(`${failed} photo${failed === 1 ? '' : 's'} did not upload — retry from the lead`);
            }
            navigate(`/leads/${created.lead.id}`, { replace: true });
        },
        onError: (error) => {
            // Deliberately stay on the form. A vanishing toast plus a lost form is
            // how leads disappear in low-signal areas.
            const message = axios.isAxiosError(error)
                ? (error.response?.data?.detail ?? error.message)
                : 'Could not save. Check your connection and try again.';
            setSubmitError(typeof message === 'string' ? message : 'Could not save the lead');
        },
    });

    const canSubmit = values.business_name.trim().length > 0 && !mutation.isPending;

    return (
        // Explicit dvh height, matching NewPaymentInPage. This route renders OUTSIDE
        // MobileLayout, so nothing above it bounds the height — with only `min-h-full`
        // the flex-1 scroll area sizes to its content and `overflow-y: auto` never has
        // anything to scroll, which silently makes the form's lower half unreachable.
        <div
            className="flex flex-col bg-background"
            style={{ minHeight: '100dvh', height: '100dvh' }}
        >
            <MobileHeader title="New Lead" showBack backPath="/leads" />

            <div
                className="flex-1 overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch' }}
            >
                <div className="p-4 space-y-4 pb-8">
                    {restorePrompt && (
                        <div className="mobile-card border-primary/40 bg-primary/5">
                            <div className="flex items-start gap-3">
                                <RotateCcw className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm">Restore your unsaved lead?</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                        {restorePrompt.business_name || 'Untitled'}
                                    </p>
                                    <div className="flex gap-2 mt-3">
                                        <MobileButton
                                            size="sm"
                                            onClick={() => {
                                                setValues({ ...EMPTY, ...restorePrompt });
                                                setRestorePrompt(null);
                                            }}
                                        >
                                            Restore
                                        </MobileButton>
                                        <MobileButton
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => {
                                                clearLeadDraft();
                                                setRestorePrompt(null);
                                            }}
                                        >
                                            Discard
                                        </MobileButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <MobileInput
                        label="Business name"
                        placeholder="e.g. Sri Krishna Mess"
                        value={values.business_name}
                        onChange={(e) => set('business_name', e.target.value)}
                        autoFocus
                    />

                    <div>
                        <MobileInput
                            label="Phone"
                            type="tel"
                            inputMode="numeric"
                            placeholder="10-digit mobile"
                            value={values.phone}
                            onChange={(e) => set('phone', e.target.value)}
                            onBlur={runDuplicateCheck}
                        />
                        {duplicate && <DuplicateNotice warning={duplicate} />}
                    </div>

                    <MobileInput
                        label="Contact person"
                        placeholder="Owner / manager name"
                        value={values.contact_person}
                        onChange={(e) => set('contact_person', e.target.value)}
                    />

                    {showAltPhone ? (
                        <MobileInput
                            label="Alternate phone"
                            type="tel"
                            inputMode="numeric"
                            value={values.alt_phone}
                            onChange={(e) => set('alt_phone', e.target.value)}
                        />
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowAltPhone(true)}
                            className="flex items-center gap-1.5 text-sm text-primary font-medium"
                        >
                            <Plus className="h-4 w-4" /> Add another number
                        </button>
                    )}

                    <ChipRow
                        label="Business type"
                        options={LEAD_TYPE_CHIPS.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] }))}
                        value={values.lead_type}
                        onChange={(v) => set('lead_type', v)}
                    />

                    <div className="grid grid-cols-2 gap-3">
                        <MobileInput
                            label="Area"
                            placeholder="e.g. Medavakkam"
                            value={values.area}
                            onChange={(e) => set('area', e.target.value)}
                        />
                        <MobileInput
                            label="Pincode"
                            inputMode="numeric"
                            maxLength={6}
                            value={values.pincode}
                            onChange={(e) => set('pincode', e.target.value.replace(/\D/g, ''))}
                        />
                    </div>

                    <MobileTextarea
                        label="Address"
                        rows={2}
                        value={values.address}
                        onChange={(e) => set('address', e.target.value)}
                    />

                    {/* Location — mirrors the party capture flow, minus the replace confirm
                        (there is no existing value to protect on a new lead). */}
                    <div>
                        <p className="mobile-section-header mb-2">Location</p>
                        <button
                            type="button"
                            onClick={locating ? undefined : handleCaptureLocation}
                            className="mobile-card w-full flex items-center gap-3 active:bg-accent"
                        >
                            {locating ? (
                                <Loader2 className="h-5 w-5 text-primary animate-spin flex-shrink-0" />
                            ) : (
                                <Crosshair className="h-5 w-5 text-primary flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0 text-left">
                                <p className="text-sm font-medium">
                                    {locating
                                        ? 'Getting location…'
                                        : values.geo_location
                                          ? 'Location attached'
                                          : 'Capture current location'}
                                </p>
                                {values.geo_location && (
                                    <p className="text-xs text-muted-foreground truncate">
                                        {values.geo_location}
                                    </p>
                                )}
                            </div>
                            {values.geo_location && (
                                <MapPin className="h-4 w-4 text-green-400 flex-shrink-0" />
                            )}
                        </button>
                    </div>

                    <ChipRow
                        label="Source"
                        options={SOURCE_CHIPS.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] }))}
                        value={values.source}
                        onChange={(v) => set('source', v)}
                    />

                    <MobileInput
                        label="Est. monthly value (₹)"
                        inputMode="numeric"
                        placeholder="Optional"
                        value={values.estimated_monthly_value}
                        onChange={(e) =>
                            set('estimated_monthly_value', e.target.value.replace(/[^\d.]/g, ''))
                        }
                    />

                    <MobileTextarea
                        label="Notes"
                        rows={2}
                        placeholder="What did the owner say?"
                        value={values.notes}
                        onChange={(e) => set('notes', e.target.value)}
                    />

                    <div>
                        <p className="mobile-section-header mb-2">Next follow-up</p>
                        <div className="flex gap-2 mb-2">
                            {[
                                { label: 'Tomorrow', value: addDaysISO(1) },
                                { label: '3 days', value: addDaysISO(3) },
                                { label: '1 week', value: addDaysISO(7) },
                            ].map((opt) => (
                                <button
                                    key={opt.label}
                                    type="button"
                                    onClick={() => set('next_follow_up_date', opt.value)}
                                    className={cn(
                                        'flex-1 py-2 text-sm rounded-lg font-medium',
                                        values.next_follow_up_date === opt.value
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-secondary text-muted-foreground'
                                    )}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <input
                            type="date"
                            min={todayISO()}
                            value={values.next_follow_up_date}
                            onChange={(e) => set('next_follow_up_date', e.target.value)}
                            className="mobile-input"
                        />
                    </div>

                    <div>
                        <p className="mobile-section-header mb-2">Photos</p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                const files = Array.from(e.target.files ?? []);
                                if (files.length) setPhotos((prev) => [...prev, ...files]);
                                e.target.value = '';
                            }}
                        />
                        <div className="flex gap-2 flex-wrap">
                            {photos.map((photo, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={URL.createObjectURL(photo)}
                                        alt=""
                                        className="h-20 w-20 rounded-lg object-cover border border-border"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setPhotos((prev) => prev.filter((_, i) => i !== index))
                                        }
                                        className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
                                        aria-label="Remove photo"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="h-20 w-20 rounded-lg border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground active:bg-accent"
                            >
                                <Camera className="h-5 w-5" />
                                <span className="text-[10px]">Add</span>
                            </button>
                        </div>
                    </div>

                    {submitError && (
                        <div className="mobile-card border-destructive/50 bg-destructive/10">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-destructive">
                                        Could not save
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {submitError}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Your entry is kept on this device. Tap Save again when you
                                        have signal.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* A plain flex item, not sticky: the root is now a bounded flex column,
                so this sits at the bottom on its own. No tab bar on this route to
                collide with. */}
            <div className="border-t border-border bg-background p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
                <MobileButton
                    className="w-full"
                    disabled={!canSubmit}
                    onClick={() => {
                        setSubmitError(null);
                        mutation.mutate();
                    }}
                >
                    {mutation.isPending ? 'Saving…' : 'Save lead'}
                </MobileButton>
            </div>

            <MobileBottomSheet
                open={!!captured}
                onClose={() => setCaptured(null)}
                title="Confirm location"
                heightMode="auto"
            >
                {captured && (
                    <div className="p-4 space-y-4">
                        <div>
                            <p className="text-lg font-semibold">
                                {captured.lat.toFixed(6)}, {captured.lng.toFixed(6)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Accuracy ±{Math.round(captured.accuracy)} m
                            </p>
                        </div>
                        <a
                            href={buildMapsUrl(captured.lat, captured.lng)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary font-medium"
                        >
                            <MapPin className="h-4 w-4" /> Verify in Maps
                        </a>
                        <div className="flex gap-2">
                            <MobileButton className="w-full" onClick={saveCapturedLocation}>
                                Use this location
                            </MobileButton>
                            <MobileButton
                                className="w-full"
                                variant="secondary"
                                onClick={handleCaptureLocation}
                            >
                                Retry
                            </MobileButton>
                        </div>
                    </div>
                )}
            </MobileBottomSheet>
        </div>
    );
}

function DuplicateNotice({ warning }: { warning: DuplicateWarning }) {
    const party = warning.parties[0];
    const lead = warning.leads[0];
    if (!party && !lead) return null;

    return (
        <div className="mt-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
            <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs min-w-0">
                    {party && (
                        <p className="text-yellow-200">
                            <span className="font-semibold">{party.name}</span> is already a customer
                            {party.responsible_party_name ? ` of ${party.responsible_party_name}` : ''}.
                        </p>
                    )}
                    {lead && (
                        <p className="text-yellow-200 mt-0.5">
                            Lead <span className="font-semibold">{lead.business_name}</span> already
                            has this number
                            {lead.assigned_to_name ? ` (${lead.assigned_to_name})` : ''}.
                        </p>
                    )}
                    <p className="text-muted-foreground mt-1">
                        You can still save — one owner often runs several outlets.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ChipRow({
    label,
    options,
    value,
    onChange,
}: {
    label: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <div>
            <p className="mobile-section-header mb-2">{label}</p>
            <div className="flex gap-2 flex-wrap">
                {options.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(value === opt.value ? '' : opt.value)}
                        className={cn(
                            'px-3 py-2 rounded-full text-sm font-medium transition-colors',
                            value === opt.value
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary text-muted-foreground active:bg-accent'
                        )}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
